"use client"

import styled from "styled-components"
import { breakpoints } from '@/layouts/breakpoints'

const MarketPrimitives = {}
export default MarketPrimitives

/* ----------------------------------------------------------------
   Shared card shell + typography primitives used by every concept
   card so the whole dashboard shares one visual language.
-----------------------------------------------------------------*/

export const Card = styled.section`
  border-radius: 1.5rem;
  background: var(--color-card);
  padding: 1.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid color-mix(in oklch, var(--color-border) 60%, transparent);
  display: flex;
  flex-direction: column;
`

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`

export const TitleGroup = styled.div`
  min-width: 0;
`

export const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--color-card-foreground);
  margin: 0;
  text-wrap: balance;
`

export const Subtitle = styled.p`
  margin: 0.125rem 0 0;
  font-size: 0.875rem;
  color: var(--color-muted-foreground);
`

export const BigValue = styled.p`
  font-family: var(--font-mono);
  font-size: 1.5rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-card-foreground);
  margin: 0;
`

export const Unit = styled.span`
  font-size: 1rem;
  color: var(--color-muted-foreground);
`

type Tone = "optimal" | "caution" | "alert" | "neutral"

const toneBg: Record<Tone, string> = {
  optimal: "var(--color-optimal-muted)",
  caution: "color-mix(in oklch, var(--color-caution) 22%, transparent)",
  alert: "var(--color-alert-muted)",
  neutral: "var(--color-muted)",
}

const toneFg: Record<Tone, string> = {
  optimal: "var(--color-optimal-foreground)",
  caution: "color-mix(in oklch, var(--color-caution) 55%, black)",
  alert: "var(--color-alert)",
  neutral: "var(--color-muted-foreground)",
}

const toneDot: Record<Tone, string> = {
  optimal: "var(--color-optimal)",
  caution: "var(--color-caution)",
  alert: "var(--color-alert)",
  neutral: "var(--color-muted-foreground)",
}

const BadgeBase = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  border-radius: 9999px;
  padding: 0.25rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  background: ${(p) => toneBg[p.$tone]};
  color: ${(p) => toneFg[p.$tone]};
`

const BadgeDot = styled.span<{ $tone: Tone }>`
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 9999px;
  background: ${(p) => toneDot[p.$tone]};
`

export function Badge({
  children,
  tone = "optimal",
  dot = true,
}: {
  children: React.ReactNode
  tone?: Tone
  dot?: boolean
}) {
  return (
    <BadgeBase $tone={tone}>
      {dot && <BadgeDot $tone={tone} />}
      {children}
    </BadgeBase>
  )
}

/* Stat footer grid (label + value columns) */
export const StatGrid = styled.dl<{ $cols?: number }>`
  margin: 1.25rem 0 0;
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols ?? 3}, 1fr);
  gap: 0.75rem;
  border-top: 1px solid color-mix(in oklch, var(--color-border) 70%, transparent);
  padding-top: 1rem;
`

export const StatLabel = styled.dt`
  font-size: 0.75rem;
  color: var(--color-muted-foreground);
`

export const StatValue = styled.dd<{ $tone?: Tone }>`
  margin: 0.25rem 0 0;
  font-family: var(--font-mono);
  font-size: 1rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$tone ? toneFg[p.$tone] : "var(--color-card-foreground)")};
`

export const StatText = styled.dd`
  margin: 0.25rem 0 0;
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-card-foreground);
`

/* Segmented progress bar */
export const SegBar = styled.div`
  margin-top: 1rem;
  display: flex;
  height: 0.5rem;
  width: 100%;
  gap: 0.25rem;
  overflow: hidden;
`

export const SegBarPart = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  border-radius: 9999px;
  width: ${(p) => p.$width}%;
  background: ${(p) => p.$color};
`

/* Generic list + row used by feed-style cards */
export const List = styled.ul`
  margin: 1.25rem 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

export const Row = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border-radius: 1rem;
  background: color-mix(in oklch, var(--color-muted) 50%, transparent);
  padding: 0.75rem 1rem;
  border: 1px solid color-mix(in oklch, var(--color-border) 50%, transparent);
`

export const RowInfo = styled.div`
  min-width: 0;
`

export const RowName = styled.p`
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-card-foreground);
`

export const RowMeta = styled.p`
  margin: 0.0625rem 0 0;
  font-size: 0.75rem;
  color: var(--color-muted-foreground);
`

export const RowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
`

export const MonoValue = styled.span<{ $tone?: Tone }>`
  font-family: var(--font-mono);
  font-size: 0.9375rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$tone ? toneFg[p.$tone] : "var(--color-card-foreground)")};
`

/* Slider-style track with a triangle marker (reused for gauges) */
export const Track = styled.div`
  position: relative;
  margin: 1.75rem 0 0.5rem;
`

export const TrackBase = styled.div`
  height: 0.375rem;
  width: 100%;
  border-radius: 9999px;
  background: var(--color-muted);
`

export const TrackFill = styled.div<{ $width: number; $color?: string }>`
  height: 100%;
  border-radius: 9999px;
  width: ${(p) => p.$width}%;
  background: ${(p) => p.$color ?? "var(--color-optimal)"};
`

export const GradientTrack = styled.div`
  height: 0.375rem;
  width: 100%;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    var(--color-alert),
    var(--color-caution),
    var(--color-optimal)
  );
`

export const Scale = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--color-muted-foreground);
`

const MarkerWrap = styled.div<{ $left: number }>`
  position: absolute;
  top: -0.375rem;
  left: ${(p) => p.$left}%;
  transform: translateX(-50%);
`

export function Marker({
  percent,
  color = "var(--color-optimal)",
}: {
  percent: number
  color?: string
}) {
  return (
    <MarkerWrap $left={percent} aria-hidden>
      <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
        <path d="M6 8 0 0h12L6 8Z" fill={color} />
      </svg>
    </MarkerWrap>
  )
}

/* Lightweight inline sparkline */
export function Sparkline({
  points,
  color = "var(--color-optimal)",
  width = 72,
  height = 30,
}: {
  points: number[]
  color?: string
  width?: number
  height?: number
}) {
  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1
  const step = width / (points.length - 1)
  const coords = points.map((p, i) => ({
    x: i * step,
    y: height - ((p - min) / range) * (height - 6) - 3,
  }))
  const d = coords
    .map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
    .join(" ")
  const last = coords[coords.length - 1]
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden style={{overflow: 'visible'}}>
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last.x} cy={last.y} r={3} fill={color} />
    </svg>
  )
}

/* Radial gauge used for scores (fear/greed, sentiment, etc.) */
export function RadialGauge({
  value,
  max = 100,
  color = "var(--color-optimal)",
  size = 132,
  label,
}: {
  value: number
  max?: number
  color?: string
  size?: number
  label?: string
}) {
  const stroke = 12
  const r = (size - stroke) / 2
  const cx = size / 2
  const cy = size / 2
  const circ = Math.PI * r
  const pct = Math.min(value / max, 1)
  return (
    <svg width={size} height={size / 2 + 12} viewBox={`0 0 ${size} ${size / 2 + 12}`} aria-hidden>
      <path d={`M ${stroke / 2} ${cy} A ${r} ${r} 0 0 1 ${size - stroke / 2} ${cy}`} fill="none" stroke="var(--color-muted)" strokeWidth={stroke} strokeLinecap="round" />
      <path d={`M ${stroke / 2} ${cy} A ${r} ${r} 0 0 1 ${size - stroke / 2} ${cy}`} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} />
      <text x={cx} y={cy - 6} textAnchor="middle" fontFamily="var(--font-mono)" fontSize="26" fontWeight="600" fill="var(--color-card-foreground)">{value}</text>
      {label && <text x={cx} y={cy + 10} textAnchor="middle" fontSize="11" fill="var(--color-muted-foreground)">{label}</text>}
    </svg>
  )
}

/* Compact composition primitives for the market card variants. */
export const VariantCard = styled.section`
  min-width: 0; overflow: hidden; border-radius: 1.5rem;
  border: 1px solid color-mix(in oklch, var(--color-border) 60%, transparent);
  background: var(--color-card); color: var(--color-card-foreground);
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`
export const Pad = styled.div`padding: 1.5rem;`
export const Heading = styled.h2`margin: .25rem 0 0; font-size: 1.25rem; font-weight: 600; letter-spacing: -.02em; text-wrap: balance;`
export const Meta = styled.p`margin: .25rem 0 0; font-size: .8125rem; line-height: 1.5; color: var(--color-muted-foreground);`
export const Label = styled.p`margin: 0; font-size: .6875rem; line-height: 1.4; color: var(--color-muted-foreground);`
export const Value = styled.p`margin: .2rem 0 0; font-family: var(--font-mono); font-size: 1.125rem; font-weight: 650; letter-spacing: -.03em; font-variant-numeric: tabular-nums;`
export const Mono = styled.span<{ $tone?: "good" | "bad" | "warn" | "muted" }>`
  font-family: var(--font-mono); font-variant-numeric: tabular-nums;
  color: ${(p) => p.$tone === "good" ? "var(--color-optimal-foreground)" : p.$tone === "bad" ? "var(--color-alert)" : p.$tone === "warn" ? "var(--color-caution)" : p.$tone === "muted" ? "var(--color-muted-foreground)" : "var(--color-card-foreground)"};
`
export const Topline = styled.div`display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem;`
export const Split = styled.div`display: flex; align-items: center; justify-content: space-between; gap: .75rem;`
export const Stack = styled.div`display: flex; flex-direction: column; gap: .75rem;`
export const Pill = styled.span<{ $tone?: "good" | "bad" | "warn" }>`
  display: inline-flex; align-items: center; border-radius: 9999px; padding: .3rem .65rem; white-space: nowrap; font-size: .75rem; font-weight: 650;
  background: ${(p) => p.$tone === "bad" ? "var(--color-alert-muted)" : p.$tone === "warn" ? "color-mix(in oklch, var(--color-caution) 18%, transparent)" : "var(--color-optimal-muted)"};
  color: ${(p) => p.$tone === "bad" ? "var(--color-alert)" : p.$tone === "warn" ? "var(--color-caution)" : "var(--color-optimal-foreground)"};
`
export const Grid = styled.div<{ $cols?: number }>`display: grid; grid-template-columns: repeat(${(p) => p.$cols ?? 2}, minmax(0, 1fr));`
export const Cell = styled.div`min-width: 0; padding: 1rem; border-right: 1px solid color-mix(in oklch, var(--color-border) 55%, transparent); border-top: 1px solid color-mix(in oklch, var(--color-border) 55%, transparent); &:last-child { border-right: 0; }`
export const SoftPanel = styled.div`border-radius: 1rem; border: 1px solid color-mix(in oklch, var(--color-border) 52%, transparent); background: color-mix(in oklch, var(--color-muted) 45%, transparent); padding: 1rem;`

/* Inplace feature visuals — browser mockup shell */
export const MockBrowser = styled.div`
  background: #fff;
  border-radius: 16px;
  right: -10px;;
  box-shadow:
    0 2px 4px rgba(0,0,0,0.04),
    0 8px 24px rgba(0,0,0,0.08),
    0 32px 64px rgba(0,0,0,0.1);

  @media (max-width: 768px) {
    right: 0;
  }
  width: 100%;
  position: relative;

  &:before {
    position: absolute;
    border-radius: 16px;
    opacity: 1;
    background-image: url('${props => props.$path}');
    background-size: cover;
    width: calc(100% + 100px);
    height: calc(100% + 150px);
    top: -75px;
    left: -50px;
    z-index: -2;
    content: ' ';

    @media (max-width: 768px) {
      height: calc(100% + 75px);
      top: -34.5px;
    }
  }
`;

export const BrowserContent = styled.div`
  padding: 32px;

  @media (max-width: ${breakpoints.small}) {
    padding: 22px 14px;
  }
`;
export const Rail = styled.div`position: relative; height: .45rem; overflow: hidden; border-radius: 9999px; background: var(--color-muted);`
export const RailFill = styled.div<{ $width: number; $tone?: "good" | "bad" | "warn" }>`height: 100%; width: ${(p) => p.$width}%; border-radius: inherit; background: ${(p) => p.$tone === "bad" ? "var(--color-alert)" : p.$tone === "warn" ? "var(--color-caution)" : "var(--color-optimal)"};`
const TinyBars = styled.div`display: flex; height: 3.25rem; align-items: flex-end; gap: .25rem;`
const TinyBar = styled.div<{ $height: number; $tone?: "good" | "bad" | "warn" }>`min-width: .25rem; flex: 1; height: ${(p) => p.$height}%; border-radius: .2rem .2rem 0 0; background: ${(p) => p.$tone === "bad" ? "var(--color-alert)" : p.$tone === "warn" ? "var(--color-caution)" : "var(--color-optimal)"}; opacity: .82;`
export function MiniBars({ values, tone = "good" }: { values: number[]; tone?: "good" | "bad" | "warn" }) { const max = Math.max(...values); return <TinyBars aria-hidden>{values.map((value, index) => <TinyBar key={`${value}-${index}`} $height={(value / max) * 100} $tone={tone} />)}</TinyBars> }
export function Ring({ value, label, tone = "good" }: { value: number; label: string; tone?: "good" | "bad" | "warn" }) { const color = tone === "bad" ? "var(--color-alert)" : tone === "warn" ? "var(--color-caution)" : "var(--color-optimal)"; return <div style={{ display: "grid", placeItems: "center", position: "relative" }}><svg width="108" height="108" viewBox="0 0 108 108" aria-hidden><circle cx="54" cy="54" r="44" fill="none" stroke="var(--color-muted)" strokeWidth="9"/><circle cx="54" cy="54" r="44" fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" pathLength="100" strokeDasharray={`${value} 100`} transform="rotate(-90 54 54)"/></svg><div style={{ position: "absolute", textAlign: "center" }}><Value>{value}</Value><Label>{label}</Label></div></div> }
export const Matrix = styled.div`display: grid; grid-template-columns: repeat(6, 1fr); gap: .3rem;`
export const MatrixCell = styled.div<{ $level: number; $negative?: boolean }>`aspect-ratio: 1; border-radius: .3rem; background: ${(p) => p.$negative ? `color-mix(in oklch, var(--color-alert) ${20 + p.$level * 16}%, var(--color-muted))` : `color-mix(in oklch, var(--color-optimal) ${20 + p.$level * 16}%, var(--color-muted))`};`
